import { useEffect, useRef, useState } from "react";

import { IgTooltip } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import { TriangleAlert } from "lucide-react";

import { cn } from "@src/lib/utils";

export type AttachmentTileStatus = "uploading" | "uploaded" | "error";

interface AttachmentTileProps {
  url?: string;
  filename: string;
  mediaType: string;
  /** Upload lifecycle indicator. Only used by the composer; history calls
   * have already completed and pass `undefined` or `"uploaded"`. */
  status?: AttachmentTileStatus;
  /** Tooltip + overlay text for `status: "error"`. */
  errorMessage?: string;
  /** When provided, renders an X button in the top-right corner on hover
   * and turns the tile into a non-link `<div>` (clicking the tile body no
   * longer opens the URL — that interaction is for chat history where the
   * tile is read-only). */
  onRemove?: () => void;
}

/**
 * Single square attachment block, used by both:
 *  - chat history (`UserAttachmentGroup`) — read-only, tile is an `<a>`
 *    that opens the file URL in a new tab.
 *  - composer (`ChatComposerAttachments`) — interactive, tile has a
 *    remove button, shows upload status, no link-out on body click.
 *
 * Content-aware preview:
 *  - `image/*` → real thumbnail with skeleton loader while the URL fetches
 *  - `application/pdf` → PDF icon + filename badge
 *  - `text/csv` → spreadsheet icon + filename badge
 *  - everything else → generic file icon + filename badge
 *
 * Sizing comes from the surrounding grid cell (`w-full` / `aspect-square`);
 * caller controls how many tiles fit per row.
 */
export const AttachmentTile = ({
  url,
  filename,
  mediaType,
  status,
  errorMessage,
  onRemove,
}: AttachmentTileProps) => {
  const isImage = mediaType?.startsWith("image/");
  const isPdf = mediaType === "application/pdf";
  const isCsv = mediaType === "text/csv";
  const interactive = !onRemove && !!url;

  const body = (
    <>
      {isImage ? (
        <ImagePreview url={url ?? ""} filename={filename} />
      ) : (
        <NonImagePreview kind={isPdf ? "pdf" : isCsv ? "csv" : "generic"} />
      )}

      {/* Filename badge — non-image tiles only. Images stay visually clean
          since the preview already communicates what the file is; the
          filename is still available via the title attribute on the
          wrapper for accessibility. */}
      {!isImage && status !== "error" && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 px-2 py-1.5 text-[11px] leading-tight text-foreground">
          <div className="truncate font-medium">{filename}</div>
        </div>
      )}

      {/* Status overlays — composer-only. */}
      {status === "uploading" && <UploadingOverlay />}
      {status === "error" && <ErrorOverlay />}

      {/* Remove button — composer-only. Stops propagation so it doesn't
          trigger the tile's enclosing handler if any. */}
      {onRemove && <RemoveButton onClick={onRemove} />}
    </>
  );

  const className = cn(
    "group/tile relative block aspect-square w-full overflow-hidden rounded-lg border border-border bg-secondary/40 transition-colors",
    interactive && "hover:bg-secondary/60"
  );

  const inner = interactive ? (
    <a
      className={className}
      href={url}
      rel="noopener noreferrer"
      target="_blank"
    >
      {body}
    </a>
  ) : (
    <div className={className}>{body}</div>
  );

  // Tooltip only on errored tiles, showing only the reason. Non-error tiles
  // need no hover info — the preview + filename badge (non-image) already
  // communicate everything. `wrapperStyle` keeps the tile sized to its
  // grid cell despite IgTooltip's inline-span wrapper.
  if (status === "error" && errorMessage) {
    return (
      <IgTooltip
        title={errorMessage}
        wrapperStyle={{ display: "block", width: "100%" }}
      >
        {inner}
      </IgTooltip>
    );
  }
  return inner;
};

/* ─── Image preview with skeleton ──────────────────────────────────────── */

const ImagePreview = ({ url, filename }: { url: string; filename: string }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Browser-unfetchable URLs (e.g. `s3://...`) on an optimistic message would
  // immediately error and the tile would stick on "Broken" even after the
  // server echo replaces the URL with a presigned https://. Treat those as
  // "still loading" (skeleton) and only render the <img> once we have a real
  // URL. Reset state whenever the URL prop changes so a swap from a blob URL
  // → presigned URL retries the load cleanly.
  const isFetchable = /^(https?:|blob:|data:)/.test(url);

  useEffect(() => {
    // Reset `errored` so a new URL can fail-fast on its own merits if the
    // previous one had failed. Deliberately do NOT reset `loaded`: when
    // the browser swaps `src` in place, the previously-rendered frame
    // stays painted until the new image is fetched — keeping `loaded`
    // true avoids a skeleton flash on the optimistic blob → presigned
    // https swap. `onLoad`/`onError` on the <img> will update state when
    // the swap completes (no-op if `loaded` was already true).
    setErrored(false);
    // If the image was already resolved by the browser (cached, or a blob
    // URL that resolves synchronously), `onLoad` won't fire after the
    // listener attaches — we'd be stuck on skeleton forever. Sync state
    // from the DOM here for that case.
    const img = imgRef.current;
    if (img?.complete) {
      if (img.naturalHeight > 0) {
        setLoaded(true);
      } else {
        setErrored(true);
      }
    }
  }, [url]);

  if (!isFetchable) {
    return <SkeletonShimmer />;
  }

  return (
    <>
      {!loaded && !errored && <SkeletonShimmer />}
      {errored ? (
        <NonImagePreview kind="broken" />
      ) : (
        <img
          alt={filename}
          className={cn(
            "h-full w-full object-cover transition-opacity duration-200",
            loaded ? "opacity-100" : "opacity-0"
          )}
          loading="lazy"
          onError={() => setErrored(true)}
          onLoad={() => setLoaded(true)}
          ref={imgRef}
          src={url}
        />
      )}
    </>
  );
};

/* ─── Non-image preview (icon centred on the tile) ────────────────────── */

const NonImagePreview = ({
  kind,
}: {
  kind: "pdf" | "csv" | "generic" | "broken";
}) => {
  const { icon, label, accent } = previewMeta(kind);
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-1 p-2",
        accent
      )}
    >
      {icon}
      <span className="text-[10px] font-semibold uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
};

const previewMeta = (kind: "pdf" | "csv" | "generic" | "broken") => {
  switch (kind) {
    case "pdf":
      return {
        label: "PDF",
        accent: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
        icon: <DocumentGlyph />,
      };
    case "csv":
      return {
        label: "CSV",
        accent:
          "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
        icon: <SheetGlyph />,
      };
    case "broken":
      return {
        label: "Broken",
        accent: "bg-muted text-muted-foreground",
        icon: <BrokenImageGlyph />,
      };
    case "generic":
    default:
      return {
        label: "File",
        accent: "bg-muted text-muted-foreground",
        icon: <DocumentGlyph />,
      };
  }
};

/* ─── Status overlays (composer-only) ──────────────────────────────────── */

const UploadingOverlay = () => (
  // The whole backdrop pulses (Tailwind's `animate-pulse` ramps opacity
  // 100% → 50% → 100% on a 2s loop) so the dimming is unmistakable as
  // "processing" rather than a static tinted overlay. Base alpha is 70%
  // so the trough still leaves ~35% dimming — visible on bright photos.
  <div className="pointer-events-none absolute inset-0 animate-pulse bg-background/70" />
);

const ErrorOverlay = () => {
  // Semi-transparent backdrop dims the image to read as "unusable" while
  // still showing the user what they picked. The centred warning triangle
  // (in the theme's warning color) pops against the muted backdrop on any
  // image. The reason text lives in the wrapper's `title` so it surfaces
  // as a native tooltip on hover.
  const theme = useTheme();
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/60">
      <TriangleAlert color={theme.igIconColors?.warning} size={28} />
    </div>
  );
};

/* ─── Remove button (composer-only) ────────────────────────────────────── */

const RemoveButton = ({ onClick }: { onClick: () => void }) => (
  // Positioned just inside the tile bounds so the surrounding
  // `overflow-hidden` (needed to clip the image to the tile's rounded
  // corners) doesn't clip the button. The slight inset + drop shadow
  // keeps it visually distinct from the underlying image.
  <button
    aria-label="Remove attachment"
    className={cn(
      "absolute right-1 top-1 z-20 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full",
      "border border-border bg-background text-foreground shadow-sm",
      "opacity-0 transition-opacity group-hover/tile:opacity-100",
      "hover:bg-muted"
    )}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    type="button"
  >
    <XGlyph />
  </button>
);

/* ─── Skeleton shimmer ─────────────────────────────────────────────────── */

const SkeletonShimmer = () => (
  <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-muted via-muted/60 to-muted" />
);

/* ─── Icons ────────────────────────────────────────────────────────────── */

const DocumentGlyph = () => (
  <svg
    aria-hidden
    className="h-7 w-7"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={1.5}
    viewBox="0 0 24 24"
  >
    <path d="M14 3v5h5" />
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9l5 5v11a2 2 0 0 1-2 2Z" />
    <path d="M8 13h8M8 17h8M8 9h2" />
  </svg>
);

const SheetGlyph = () => (
  <svg
    aria-hidden
    className="h-7 w-7"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={1.5}
    viewBox="0 0 24 24"
  >
    <rect height="16" rx="2" width="18" x="3" y="4" />
    <path d="M3 10h18M3 16h18M9 4v16M15 4v16" />
  </svg>
);

const BrokenImageGlyph = () => (
  <svg
    aria-hidden
    className="h-7 w-7"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={1.5}
    viewBox="0 0 24 24"
  >
    <rect height="18" rx="2" width="18" x="3" y="3" />
    <path d="m3 16 5-5 4 4M14 13l2-2 5 5" />
    <path d="m4 20 6-6" strokeDasharray="2 2" />
  </svg>
);

const XGlyph = () => (
  <svg
    aria-hidden
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
